/**
 * RecommendationsTab Component (Simplified)
 * 
 * Shows only recommendation card with header.
 * All details are in the PDF report only.
 */
import React from 'react';
import { RecommendationsData } from '../../types/results';

interface RecommendationsTabProps {
  loadingRecommendations: boolean;
  recommendations: RecommendationsData | null;
  profileName: string;
  onLoadRecommendations: () => void;
  onViewRecommendationsPdf: () => void;
  onDownloadRecommendationsPdf: () => void;
  generatingPdf: boolean;
}

// Get username from localStorage
const getUsername = (): string => {
  return localStorage.getItem('username') || 'Utilisateur';
};

export const RecommendationsTab: React.FC<RecommendationsTabProps> = ({
  loadingRecommendations,
  recommendations,
  profileName,
  onLoadRecommendations,
  onViewRecommendationsPdf,
  onDownloadRecommendationsPdf,
  generatingPdf
}) => {
  if (loadingRecommendations) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin inline-block w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full mb-4"></div>
        <p className="text-gray-600 font-medium">Génération de vos recommandations...</p>
        <p className="text-gray-500 text-sm mt-2">Notre IA analyse votre profil</p>
        <p className="text-gray-400 text-xs mt-4">Cela peut prendre 30-60 secondes</p>
      </div>
    );
  }

  if (!recommendations) {
    return (
      <div className="text-center py-12">
        <div className="bg-emerald-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <p className="text-gray-600 mb-4">Recommandations non encore générées</p>
        <button
          onClick={onLoadRecommendations}
          className="bg-emerald-500 text-white font-semibold py-2.5 px-8 rounded-lg hover:bg-emerald-600 transition"
        >
          Générer mes recommandations
        </button>
      </div>
    );
  }

  const username = getUsername();

  return (
    <div className="py-2">
      {/* Recommendations Card */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1 min-w-0">
            <p className="text-emerald-100 text-xs uppercase tracking-wide mb-1">Recommandations personnalisées basées sur votre profil</p>
            <h2 className="text-lg sm:text-xl font-bold leading-tight">{username}</h2>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-white bg-opacity-15 rounded-lg px-4 py-3 mb-5">
          <div className="flex items-center gap-3 text-sm">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-emerald-50 text-xs sm:text-sm">
              Ce rapport contient des recommandations carrière, des stratégies de développement et un plan d'action personnalisé.
            </p>
          </div>
        </div>

        {/* View Report Button */}
        <button
          onClick={onViewRecommendationsPdf}
          disabled={generatingPdf}
          className="w-full bg-white text-emerald-600 font-semibold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-emerald-50 transition disabled:opacity-50"
        >
          {generatingPdf ? (
            <span className="animate-spin w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full"></span>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
          <span>Voir le rapport complet</span>
        </button>

        {/* Download Link */}
        <button
          onClick={onDownloadRecommendationsPdf}
          className="w-full text-emerald-100 text-sm py-2 mt-2 flex items-center justify-center gap-2 hover:text-white transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Télécharger le PDF
        </button>
      </div>
    </div>
  );
};

export default RecommendationsTab;
